import { apiClient } from "../../api/client";

export const AIRCRAFT_SECTION_EXTRACTION_PATH =
  "/thread/v1/aero/aircraft-section-extractions" as const;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const SHA256 = /^sha256:[0-9a-f]{64}$/;

export interface AircraftSectionVersionPin {
  readonly artifactId: string;
  readonly artifactVersionId: string;
  readonly contentRevision: string;
  readonly logicalId: string;
}

export interface AircraftSectionDefinitionPin extends AircraftSectionVersionPin {
  readonly definitionKind: string;
  readonly semanticDefinitionId: string;
  readonly semanticDefinitionRevision: string;
  readonly semanticKind: string;
}

export interface AircraftSectionOccurrence {
  readonly effectiveOwnershipId: string;
  readonly occurrenceId: string;
  readonly occurrenceRevision: string;
}

export interface AircraftSectionPlane {
  readonly basis: readonly (readonly number[])[];
  readonly frame: "aircraft-body-x-forward-y-right-z-up/v1";
  readonly originM: readonly number[];
}

export interface AircraftSectionTarget {
  readonly bodyId: string;
  readonly kind: "wing" | "nacelle" | "duct" | "body" | "actuator-region";
  readonly sourceFaceIds: readonly string[];
}

export interface AircraftSectionSliceRequest {
  readonly aircraftVersionPin: AircraftSectionVersionPin;
  readonly definitionPin: AircraftSectionDefinitionPin;
  readonly extractorRevision: string;
  readonly occurrence: AircraftSectionOccurrence;
  readonly plane: AircraftSectionPlane;
  readonly poseStatePin: AircraftSectionVersionPin & {
    readonly definitionPin: AircraftSectionDefinitionPin;
    readonly stateSchema: "loom.aircraft-pose/v1";
  };
  readonly schema: "loom.aircraft-section-slice-request/v1";
  readonly settings: {
    readonly intersectionToleranceM: number;
    readonly maximumLoops: number;
    readonly maximumPointsPerLoop: number;
  };
  readonly sourceGeometryPin: AircraftSectionVersionPin;
  readonly target: AircraftSectionTarget;
}

export interface AircraftSectionExtractionCommand {
  readonly idempotency_key: string;
  readonly project_id: string;
  readonly request_id: string;
  readonly slice_request: AircraftSectionSliceRequest;
  readonly target: {
    readonly logical_id: string;
    readonly metadata: Readonly<Record<string, unknown>>;
    readonly name: string;
  };
}

export interface AircraftSectionLoop {
  readonly loopId: string;
  readonly order: number;
  readonly pointsUvM: readonly (readonly [number, number])[];
  readonly segmentOwnership: readonly {
    readonly bodyId: string;
    readonly effectiveOwnershipId: string;
    readonly faceId: string;
    readonly segmentIndex: number;
  }[];
  readonly topology: "closed" | "open";
  readonly winding: "counterclockwise" | "clockwise" | "none";
}

export interface AircraftSectionArtifact {
  readonly artifactRevision: string;
  readonly construction: {
    readonly extractorRevision: string;
    readonly method: "tessellated-plane-intersection";
    readonly receiptRevision: string;
    readonly sourceRepresentationRevision: string;
    readonly toleranceM: number;
  };
  readonly fidelity: {
    readonly evidenceRevision: string;
    readonly maximumDeviationM: number;
    readonly status: "adapted";
  };
  readonly loops: readonly AircraftSectionLoop[];
  readonly qc: {
    readonly closedLoopCount: number;
    readonly loopCount: number;
    readonly maximumClosureGapM: number;
    readonly minimumSegmentLengthM: number;
    readonly openLoopCount: number;
    readonly orientation: "verified";
    readonly ownershipCoverage: "complete";
    readonly receiptRevision: string;
    readonly selfIntersectionCount: 0;
    readonly status: "passed";
  };
  readonly schema: "loom.aircraft-section-artifact/v1";
  readonly sectionFrame: AircraftSectionPlane;
  readonly sliceRequestRevision: string;
  readonly sourceBindings: Pick<AircraftSectionSliceRequest,
    "aircraftVersionPin" | "definitionPin" | "occurrence" | "poseStatePin"
    | "sourceGeometryPin" | "target">;
}

export interface AircraftSectionExtractionResult {
  readonly artifact_id: string;
  readonly artifact_version_id: string;
  readonly content: {
    readonly request_id: string;
    readonly schema_version: "aero.aircraft-section-extraction.v1";
    readonly section_artifact: AircraftSectionArtifact;
  };
  readonly content_identity: {
    readonly algorithm: "sha256";
    readonly byte_length: number;
    readonly canonicalizer: "rfc8785";
    readonly canonicalizer_version: "1";
    readonly digest: string;
    readonly media_type: "application/vnd.thread.aero-aircraft-section-extraction+json";
  };
  readonly operation_id: string;
  readonly replayed: boolean;
}

type Row = Record<string, unknown>;

function row(value: unknown, path: string): Row {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Row;
}

function exact(value: Row, keys: readonly string[], path: string): void {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  if (actual.length !== expected.length || actual.some((key, index) => key !== expected[index])) {
    throw new Error(`${path} has an unsupported or incomplete field set`);
  }
}

function string(value: unknown, path: string): string {
  if (typeof value !== "string" || !value.length) throw new Error(`${path} must be a non-empty string`);
  return value;
}

function uuid(value: unknown, path: string): string {
  const parsed = string(value, path);
  if (!UUID.test(parsed)) throw new Error(`${path} must be a canonical lowercase UUID`);
  return parsed;
}

function sha(value: unknown, path: string): string {
  const parsed = string(value, path);
  if (!SHA256.test(parsed)) throw new Error(`${path} must be a sha256 revision`);
  return parsed;
}

function finite(value: unknown, path: string, minimum?: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)
      || (minimum !== undefined && value < minimum)) {
    throw new Error(`${path} must be a finite number`);
  }
  return value;
}

function integer(value: unknown, path: string, minimum = 0): number {
  const parsed = finite(value, path, minimum);
  if (!Number.isInteger(parsed)) throw new Error(`${path} must be an integer`);
  return parsed;
}

function oneOf<T extends string>(value: unknown, choices: readonly T[], path: string): T {
  if (typeof value !== "string" || !choices.includes(value as T)) {
    throw new Error(`${path} is unsupported`);
  }
  return value as T;
}

function pin(value: unknown, path: string): AircraftSectionVersionPin {
  const item = row(value, path);
  exact(item, ["logicalId", "artifactId", "artifactVersionId", "contentRevision"], path);
  return {
    logicalId: uuid(item.logicalId, `${path}.logicalId`),
    artifactId: uuid(item.artifactId, `${path}.artifactId`),
    artifactVersionId: uuid(item.artifactVersionId, `${path}.artifactVersionId`),
    contentRevision: sha(item.contentRevision, `${path}.contentRevision`),
  };
}

function definitionPin(value: unknown, path: string): AircraftSectionDefinitionPin {
  const item = row(value, path);
  exact(item, ["logicalId", "artifactId", "artifactVersionId", "contentRevision",
    "semanticDefinitionId", "semanticDefinitionRevision", "definitionKind", "semanticKind"], path);
  return {
    ...pin(Object.fromEntries(Object.entries(item).filter(([key]) => [
      "logicalId", "artifactId", "artifactVersionId", "contentRevision",
    ].includes(key))), path),
    semanticDefinitionId: string(item.semanticDefinitionId, `${path}.semanticDefinitionId`),
    semanticDefinitionRevision: sha(item.semanticDefinitionRevision, `${path}.semanticDefinitionRevision`),
    definitionKind: string(item.definitionKind, `${path}.definitionKind`),
    semanticKind: string(item.semanticKind, `${path}.semanticKind`),
  };
}

function occurrence(value: unknown, path: string): AircraftSectionOccurrence {
  const item = row(value, path);
  exact(item, ["occurrenceId", "effectiveOwnershipId", "occurrenceRevision"], path);
  return {
    occurrenceId: string(item.occurrenceId, `${path}.occurrenceId`),
    effectiveOwnershipId: string(item.effectiveOwnershipId, `${path}.effectiveOwnershipId`),
    occurrenceRevision: sha(item.occurrenceRevision, `${path}.occurrenceRevision`),
  };
}

function vector(value: unknown, path: string, length: 2 | 3): number[] {
  if (!Array.isArray(value) || value.length !== length) throw new Error(`${path} has invalid length`);
  return value.map((item, index) => finite(item, `${path}[${index}]`));
}

function plane(value: unknown, path: string): AircraftSectionPlane {
  const item = row(value, path);
  exact(item, ["frame", "originM", "basis"], path);
  if (item.frame !== "aircraft-body-x-forward-y-right-z-up/v1") throw new Error(`${path}.frame is unsupported`);
  if (!Array.isArray(item.basis) || item.basis.length !== 3) throw new Error(`${path}.basis is invalid`);
  return {
    frame: item.frame,
    originM: vector(item.originM, `${path}.originM`, 3) as [number, number, number],
    basis: item.basis.map((axis, index) => vector(axis, `${path}.basis[${index}]`, 3)),
  };
}

function target(value: unknown, path: string): AircraftSectionTarget {
  const item = row(value, path);
  exact(item, ["kind", "bodyId", "sourceFaceIds"], path);
  if (!Array.isArray(item.sourceFaceIds) || item.sourceFaceIds.length < 1) {
    throw new Error(`${path}.sourceFaceIds is invalid`);
  }
  const sourceFaceIds = item.sourceFaceIds.map((face, index) => string(face, `${path}.sourceFaceIds[${index}]`));
  if (new Set(sourceFaceIds).size !== sourceFaceIds.length) throw new Error(`${path}.sourceFaceIds has duplicates`);
  return {
    kind: oneOf(item.kind, ["wing", "nacelle", "duct", "body", "actuator-region"] as const, `${path}.kind`),
    bodyId: string(item.bodyId, `${path}.bodyId`), sourceFaceIds,
  };
}

function posePin(value: unknown, path: string) {
  const item = row(value, path);
  exact(item, ["logicalId", "artifactId", "artifactVersionId", "contentRevision",
    "stateSchema", "definitionPin"], path);
  if (item.stateSchema !== "loom.aircraft-pose/v1") throw new Error(`${path}.stateSchema is unsupported`);
  return {
    ...pin(Object.fromEntries(Object.entries(item).filter(([key]) => [
      "logicalId", "artifactId", "artifactVersionId", "contentRevision",
    ].includes(key))), path),
    stateSchema: item.stateSchema,
    definitionPin: definitionPin(item.definitionPin, `${path}.definitionPin`),
  } as const;
}

export const parseAircraftSectionVersionPin = pin;
export const parseAircraftSectionDefinitionPin = definitionPin;
export const parseAircraftSectionOccurrence = occurrence;
export const parseAircraftSectionPlane = plane;
export const parseAircraftSectionTarget = target;
export const parseAircraftSectionPoseStatePin = posePin;

function same(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((item, index) => same(item, right[index]));
  }
  if (typeof left === "object" && left !== null && typeof right === "object" && right !== null) {
    const leftRow = left as Row;
    const rightRow = right as Row;
    const keys = Object.keys(leftRow).sort();
    return keys.length === Object.keys(rightRow).length
      && keys.every((key) => same(leftRow[key], rightRow[key]));
  }
  return left === right;
}

function parseBindings(value: unknown, expected: AircraftSectionSliceRequest) {
  const item = row(value, "sectionArtifact.sourceBindings");
  exact(item, ["aircraftVersionPin", "definitionPin", "occurrence", "poseStatePin",
    "sourceGeometryPin", "target"], "sectionArtifact.sourceBindings");
  const parsed = {
    aircraftVersionPin: pin(item.aircraftVersionPin, "sourceBindings.aircraftVersionPin"),
    definitionPin: definitionPin(item.definitionPin, "sourceBindings.definitionPin"),
    occurrence: occurrence(item.occurrence, "sourceBindings.occurrence"),
    poseStatePin: posePin(item.poseStatePin, "sourceBindings.poseStatePin"),
    sourceGeometryPin: pin(item.sourceGeometryPin, "sourceBindings.sourceGeometryPin"),
    target: target(item.target, "sourceBindings.target"),
  };
  const expectedBindings = {
    aircraftVersionPin: expected.aircraftVersionPin, definitionPin: expected.definitionPin,
    occurrence: expected.occurrence, poseStatePin: expected.poseStatePin,
    sourceGeometryPin: expected.sourceGeometryPin, target: expected.target,
  };
  if (!same(parsed, expectedBindings)) {
    throw new Error("section artifact has a stale source binding or occurrence identity");
  }
  return parsed;
}

function parseLoop(value: unknown, order: number, expected: AircraftSectionSliceRequest): AircraftSectionLoop {
  const path = `sectionArtifact.loops[${order}]`;
  const item = row(value, path);
  exact(item, ["loopId", "order", "topology", "winding", "pointsUvM", "segmentOwnership"], path);
  const topology = oneOf(item.topology, ["closed", "open"] as const, `${path}.topology`);
  const winding = oneOf(item.winding, ["counterclockwise", "clockwise", "none"] as const, `${path}.winding`);
  if (integer(item.order, `${path}.order`) !== order || (topology === "open") !== (winding === "none")) {
    throw new Error(`${path} order, topology, or winding is inconsistent`);
  }
  if (!Array.isArray(item.pointsUvM) || item.pointsUvM.length < (topology === "closed" ? 3 : 2)
      || item.pointsUvM.length > expected.settings.maximumPointsPerLoop) {
    throw new Error(`${path}.pointsUvM has invalid loop bounds`);
  }
  const pointsUvM = item.pointsUvM.map((point, index) =>
    vector(point, `${path}.pointsUvM[${index}]`, 2) as [number, number]);
  const segmentCount = topology === "closed" ? pointsUvM.length : pointsUvM.length - 1;
  if (!Array.isArray(item.segmentOwnership) || item.segmentOwnership.length !== segmentCount) {
    throw new Error(`${path}.segmentOwnership does not cover the loop`);
  }
  const allowedFaces = new Set(expected.target.sourceFaceIds);
  const segmentOwnership = item.segmentOwnership.map((raw, index) => {
    const ownership = row(raw, `${path}.segmentOwnership[${index}]`);
    exact(ownership, ["segmentIndex", "effectiveOwnershipId", "bodyId", "faceId"], `${path}.segmentOwnership[${index}]`);
    const parsed = {
      segmentIndex: integer(ownership.segmentIndex, `${path}.segmentOwnership[${index}].segmentIndex`),
      effectiveOwnershipId: string(ownership.effectiveOwnershipId, `${path}.segmentOwnership[${index}].effectiveOwnershipId`),
      bodyId: string(ownership.bodyId, `${path}.segmentOwnership[${index}].bodyId`),
      faceId: string(ownership.faceId, `${path}.segmentOwnership[${index}].faceId`),
    };
    if (parsed.segmentIndex !== index || parsed.effectiveOwnershipId !== expected.occurrence.effectiveOwnershipId
        || parsed.bodyId !== expected.target.bodyId
        || !allowedFaces.has(parsed.faceId)) {
      throw new Error(`${path}.segmentOwnership is outside the pinned face set or occurrence`);
    }
    return parsed;
  });
  return { loopId: string(item.loopId, `${path}.loopId`), order, topology, winding,
    pointsUvM, segmentOwnership };
}

function parseArtifact(value: unknown, expected: AircraftSectionSliceRequest): AircraftSectionArtifact {
  const item = row(value, "sectionArtifact");
  exact(item, ["schema", "sliceRequestRevision", "sourceBindings", "sectionFrame", "loops",
    "construction", "fidelity", "qc", "artifactRevision"], "sectionArtifact");
  if (item.schema !== "loom.aircraft-section-artifact/v1") throw new Error("sectionArtifact.schema is unsupported");
  const bindings = parseBindings(item.sourceBindings, expected);
  if (!same(plane(item.sectionFrame, "sectionArtifact.sectionFrame"), expected.plane)) {
    throw new Error("section artifact has a stale section plane binding");
  }
  if (!Array.isArray(item.loops) || item.loops.length < 1
      || item.loops.length > expected.settings.maximumLoops) throw new Error("sectionArtifact.loops has invalid bounds");
  const loops = item.loops.map((loop, index) => parseLoop(loop, index, expected));
  if (new Set(loops.map((loop) => loop.loopId)).size !== loops.length) throw new Error("sectionArtifact.loop identities are duplicated");
  const constructionRow = row(item.construction, "sectionArtifact.construction");
  exact(constructionRow, ["method", "sourceRepresentationRevision", "extractorRevision",
    "toleranceM", "receiptRevision"], "sectionArtifact.construction");
  if (constructionRow.method !== "tessellated-plane-intersection"
      || sha(constructionRow.sourceRepresentationRevision, "construction.sourceRepresentationRevision") !== expected.sourceGeometryPin.contentRevision
      || sha(constructionRow.extractorRevision, "construction.extractorRevision") !== expected.extractorRevision) {
    throw new Error("sectionArtifact construction is unsupported or stale");
  }
  const toleranceM = finite(constructionRow.toleranceM, "construction.toleranceM", Number.MIN_VALUE);
  if (toleranceM !== expected.settings.intersectionToleranceM) throw new Error("sectionArtifact construction tolerance is stale");
  const fidelityRow = row(item.fidelity, "sectionArtifact.fidelity");
  exact(fidelityRow, ["status", "maximumDeviationM", "evidenceRevision"], "sectionArtifact.fidelity");
  if (fidelityRow.status !== "adapted") throw new Error("sectionArtifact fidelity status is unsupported");
  const maximumDeviationM = finite(fidelityRow.maximumDeviationM, "fidelity.maximumDeviationM", 0);
  if (maximumDeviationM > toleranceM) throw new Error("sectionArtifact fidelity exceeds extraction tolerance");
  const qcRow = row(item.qc, "sectionArtifact.qc");
  exact(qcRow, ["loopCount", "closedLoopCount", "openLoopCount", "selfIntersectionCount",
    "maximumClosureGapM", "minimumSegmentLengthM", "orientation", "ownershipCoverage",
    "status", "receiptRevision"], "sectionArtifact.qc");
  const closed = loops.filter((loop) => loop.topology === "closed").length;
  if (qcRow.status !== "passed" || qcRow.ownershipCoverage !== "complete"
      || qcRow.orientation !== "verified" || qcRow.selfIntersectionCount !== 0
      || integer(qcRow.loopCount, "qc.loopCount") !== loops.length
      || integer(qcRow.closedLoopCount, "qc.closedLoopCount") !== closed
      || integer(qcRow.openLoopCount, "qc.openLoopCount") !== loops.length - closed) {
    throw new Error("sectionArtifact QC status is not passed and complete");
  }
  const maximumClosureGapM = finite(qcRow.maximumClosureGapM, "qc.maximumClosureGapM", 0);
  const minimumSegmentLengthM = finite(qcRow.minimumSegmentLengthM, "qc.minimumSegmentLengthM", Number.MIN_VALUE);
  if (maximumClosureGapM > toleranceM) throw new Error("sectionArtifact QC closure exceeds tolerance");
  return {
    schema: item.schema, sliceRequestRevision: sha(item.sliceRequestRevision, "sectionArtifact.sliceRequestRevision"),
    sourceBindings: bindings, sectionFrame: expected.plane, loops,
    construction: { method: constructionRow.method,
      sourceRepresentationRevision: expected.sourceGeometryPin.contentRevision,
      extractorRevision: expected.extractorRevision, toleranceM,
      receiptRevision: sha(constructionRow.receiptRevision, "construction.receiptRevision") },
    fidelity: { status: fidelityRow.status, maximumDeviationM,
      evidenceRevision: sha(fidelityRow.evidenceRevision, "fidelity.evidenceRevision") },
    qc: { loopCount: loops.length, closedLoopCount: closed, openLoopCount: loops.length - closed,
      selfIntersectionCount: 0, maximumClosureGapM, minimumSegmentLengthM,
      orientation: "verified", ownershipCoverage: "complete", status: "passed",
      receiptRevision: sha(qcRow.receiptRevision, "qc.receiptRevision") },
    artifactRevision: sha(item.artifactRevision, "sectionArtifact.artifactRevision"),
  };
}

export function parseAircraftSectionExtractionResult(
  value: unknown,
  expected: { readonly requestId: string; readonly sliceRequest: AircraftSectionSliceRequest },
): AircraftSectionExtractionResult {
  const envelope = row(value, "aircraft section extraction envelope");
  exact(envelope, ["schema_version", "request_id", "data", "page", "provenance", "warnings"],
    "aircraft section extraction envelope");
  if (envelope.schema_version !== "thread.api.v1") throw new Error("Thread envelope schema is unsupported");
  uuid(envelope.request_id, "envelope.request_id");
  if (envelope.page !== null) throw new Error("write response page must be null");
  const provenance = row(envelope.provenance, "envelope.provenance");
  exact(provenance, ["source", "projection_version", "schema_migration"], "envelope.provenance");
  if (provenance.source !== "thread_core" || typeof provenance.projection_version !== "string"
      || typeof provenance.schema_migration !== "string") throw new Error("Thread provenance is unsupported");
  if (!Array.isArray(envelope.warnings) || envelope.warnings.some((item) => typeof item !== "string")) {
    throw new Error("Thread envelope warnings are invalid");
  }
  const data = row(envelope.data, "aircraft section extraction data");
  exact(data, ["operation_id", "artifact_id", "artifact_version_id", "content_identity",
    "replayed", "content", "content_summary", "content_omitted"], "aircraft section extraction data envelope");
  if (data.content_summary !== null || data.content_omitted !== false) {
    throw new Error("section extraction content must be present and unsummarized");
  }
  const content = row(data.content, "aircraft section extraction content");
  exact(content, ["schema_version", "request_id", "section_artifact"], "aircraft section extraction content");
  if (content.schema_version !== "aero.aircraft-section-extraction.v1") throw new Error("section extraction schema is unsupported");
  if (uuid(content.request_id, "content.request_id") !== expected.requestId) {
    throw new Error("request identity is stale");
  }
  const identity = row(data.content_identity, "content_identity");
  exact(identity, ["algorithm", "digest", "canonicalizer", "canonicalizer_version",
    "byte_length", "media_type", "source_content_hash"], "content_identity");
  if (identity.algorithm !== "sha256" || identity.canonicalizer !== "rfc8785"
      || identity.canonicalizer_version !== "1"
      || identity.source_content_hash !== null
      || identity.media_type !== "application/vnd.thread.aero-aircraft-section-extraction+json") {
    throw new Error("content_identity authority fields are unsupported");
  }
  const digest = string(identity.digest, "content_identity.digest");
  if (!/^[0-9a-f]{64}$/.test(digest)) throw new Error("content_identity.digest is invalid");
  if (typeof data.replayed !== "boolean") throw new Error("replayed must be boolean");
  return {
    operation_id: uuid(data.operation_id, "operation_id"),
    artifact_id: uuid(data.artifact_id, "artifact_id"),
    artifact_version_id: uuid(data.artifact_version_id, "artifact_version_id"),
    replayed: data.replayed,
    content_identity: { algorithm: "sha256", digest, canonicalizer: "rfc8785",
      canonicalizer_version: "1", byte_length: integer(identity.byte_length, "content_identity.byte_length", 1),
      media_type: "application/vnd.thread.aero-aircraft-section-extraction+json" },
    content: { schema_version: content.schema_version, request_id: expected.requestId,
      section_artifact: parseArtifact(content.section_artifact, expected.sliceRequest) },
  };
}

export async function requestAircraftSectionExtraction(
  command: AircraftSectionExtractionCommand,
  signal: AbortSignal,
): Promise<AircraftSectionExtractionResult> {
  uuid(command.project_id, "project_id");
  uuid(command.request_id, "request_id");
  const outputLogicalId = uuid(command.target.logical_id, "target.logical_id");
  const inputLogicalIds = [command.slice_request.aircraftVersionPin.logicalId,
    command.slice_request.definitionPin.logicalId, command.slice_request.poseStatePin.logicalId,
    command.slice_request.sourceGeometryPin.logicalId];
  if (inputLogicalIds.includes(outputLogicalId)) {
    throw new Error("section output and every input require distinct logical IDs");
  }
  const value = await apiClient(AIRCRAFT_SECTION_EXTRACTION_PATH, {
    method: "POST", body: command, signal,
  });
  return parseAircraftSectionExtractionResult(value, {
    requestId: command.request_id, sliceRequest: command.slice_request,
  });
}
